import React from 'react'

import { IConfigMarket } from '@Redux/interfaces'
import { EditIcon } from '@Static/icons/AccountMenuIcon'
import { CloseIcon } from '@Static/icons/ArrowIcons'
import { BellIcon } from '@Static/icons/FooterIcons'
import { MarginRefreshIcon } from '@Static/icons/MarginTransferModalIcons'

import { formatServerDate } from '@App/helpers/time'
import { useAppDispatch } from '@App/hooks'
import { Card } from '@App/ui/crypto_exchange/desktop/components/common/Card'

import { MODAL_TYPES } from '@Redux/slices/modals/enums'
import { setModal } from '@Redux/slices/modals/ModalSlice'
import {sendUpdatePriceAlert} from "@Redux/slices/sockets/ProxyServiceConnectionSlice";

import { Button } from '@UI/crypto_exchange/desktop/components/controls/Button'
import { CurrencyRateLine } from '@UI/crypto_exchange/desktop/pages/all_alerts/components/CurrencyRateLine'
import { Column, Table } from '@UI/crypto_exchange/desktop/pages/discover/components/Table'
import { AlertTypeMarker } from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/components/AlertTypeMarker/AlertTypeMarker'
import { AlertType } from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/types'

import s from './styles.module.scss'

export const Alerts = ({
  alerts,
  onRowClick,
  markets,
  openAlert,
}: {
  header?: React.ReactNode
  alerts: AlertType[]
  onRowClick?: (event: AlertType) => void
  markets: IConfigMarket[]
  openAlert: (market: IConfigMarket) => void
}) => {
  const dispatch = useAppDispatch()

  const openRemoveModal = (ids: string[]) => {
    dispatch(
      setModal({
        modalType: MODAL_TYPES.REMOVE_ALERT_MODAL,
        data: { ids },
      }),
    )
  }

  const openEditModal = (data: AlertType) => {
    const market = markets.find((item) => item.id.toString() === data.market_id.toString())
    if (!market) return

    dispatch(
      setModal({
        modalType: MODAL_TYPES.EDIT_ALERT_MODAL,
        data: {
          prevData: data,
          targetCurrency: market.target_currency,
          sourceCurrency: market.source_currency,
          s,
        },
      }),
    )
  }

  const refreshAlert = (id: string) => {
    dispatch(sendUpdatePriceAlert({uuid: id}))
  }

  const columns: Column<AlertType>[] = [
    {
      title: 'Price type',
      key: 'side',
      maxWidth: '120px',
      render: (v) => <AlertTypeMarker className={s.typeConnection} type={v} />,
    },
    {
      title: 'Alert price',
      key: 'execution_price',
      headerAlign: 'end',
      maxWidth: '180px',
      render: (v) => (
        <span className={s.flexEnd}>
          <span>{v}</span>
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'completed_at',
      maxWidth: '200px',
      render: (_, row ) => {
        return (
          <>
            {row.completed_at && (
              <div className={s.triggerTime}>
                <BellIcon width={16} height={16} /> {formatServerDate(row.completed_at)}
              </div>
            )}
          </>
        )
      },
    },
    {
      title: 'Note',
      key: 'note',
      render: (v) => <span>{v}</span>,
    },
    {
      title: '',
      key: 'note',
      maxWidth: 'min-content',
      render: (_, row) => (
        <div className={s.controlContainer}>
          {row.completed_at && (
            <Button variant="ghost" className={s.button} onClick={() => refreshAlert(row.uuid)}>
              <MarginRefreshIcon width={16} height={16} />
            </Button>
          )}
          <Button variant="ghost" className={s.button} onClick={() => openEditModal(row)}>
            <EditIcon width={16} height={16} />
          </Button>
          <Button
            variant="ghost"
            className={s.button}
            onClick={() => {
              openRemoveModal([row.uuid])
            }}
          >
            <CloseIcon width={16} height={16} />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <>
      <Card className={s.container}>
        <Table
          columns={columns}
          data={alerts}
          rowKey={(row) => row.uuid}
          headerSeparated={false}
          groupBy={{
            field: 'market_id',
            stickyHeader: true,
            renderSectionHeader: (sectionValue) => {
              const market = markets.find((item) => item.id.toString() === sectionValue.toString())
              if (!market) return
              return <CurrencyRateLine key={market.id} market={market} openAlert={openAlert} alerts={alerts} />
            },
          }}
          rowHoverVariant="colorized"
          bodyClassName={s.tableBody}
          className={s.tableBg}
          onRowClick={onRowClick}
          rowClassName={() => s.tableBodyRow}
        />
      </Card>
    </>
  )
}
